import { ReactElement } from 'react'
import { ContentCut } from '@mui/icons-material'
import { Button, Divider, Stack, Typography } from '@mui/material'
import useCreateClipPlaneImportHelper from '@editor/components/Controls/ClipPlaneControls/useCreateClipPlaneImportHelper'
import { CorneredSection } from '@ui2/structure'
import IFCTransparencyVisibilitySwitches from './components/IFCTransparencyVisibilitySwitches'

interface Props {
  /** Whether to offer the "Import-Helfer" clip-plane tool (default true). */
  showImportHelper?: boolean
}

/**
 * Shared "Tools" section for the IFC import/edit steps. Always offers the "IFC transparent" switch
 * (the same `gltfModelTranslucent` toggle the steps set automatically on mount, so the user can
 * override it) and an "IFC ausblenden" switch (the `showGltfModel` visibility toggle — while the
 * model is hidden, the transparency switch is moot and disabled), and optionally the
 * "Import-Helfer" — two linked z-plane clip planes (one through the IFC model, one through the
 * imported model) that make it easy to see which elements have already been imported.
 */
const IFCImportToolsSection = ({ showImportHelper = true }: Props): ReactElement => {
  const createImportHelper = useCreateClipPlaneImportHelper()

  return (
    <CorneredSection title="Tools" collapsible>
      <Stack spacing={2}>
        {showImportHelper && (
          <>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Der Import-Helfer legt zwei Schnittebenen in der z-Ebene an, die das IFC-Modell und
                das importierte Modell schneiden. So ist auf einen Blick erkennbar, welche Elemente
                bereits importiert wurden. Verschieben Sie die Schnittebenen, um verschiedene Höhen
                zu prüfen.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCut />}
                onClick={createImportHelper}
                sx={{ alignSelf: 'flex-start' }}
                data-cy="btn-ifc-tools-import-helper"
              >
                Import-Helfer erstellen
              </Button>
            </Stack>
            <Divider />
          </>
        )}
        <IFCTransparencyVisibilitySwitches />
      </Stack>
    </CorneredSection>
  )
}

export default IFCImportToolsSection
